import { FC, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import { ReactComponent as AnalyzingIcon } from 'src/assets/svg/analyzing-48.svg'
import { ReactComponent as EmptyComparisonIcon } from 'src/assets/svg/empty-comparison-48.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning-triangle-48.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import {
  CampaignDetailHeader,
  CampaignDetailView,
} from 'src/pages/ThemeDetail/CampaignDetailHeader'
import {
  useCreateDataComparisonMutation,
  useDeleteDataComparisonMutation,
} from 'src/reactQuery/useDataComparisonMutations'
import { useGetDataComparisonQuery } from 'src/reactQuery/useGetDataComparisonQuery'
import { useGetDataComparisonsQuery } from 'src/reactQuery/useGetDataComparisonsQuery'
import { DataComparisonApiService } from 'src/services/dataComparisonApi'
import { cn } from 'src/utils/cn'
import { ComparisonFilters } from './ComparisonFilters'
import { ComparisonHistory } from './ComparisonHistory'
import { DailyMentions } from './DailyMentions'
import { KeyMetricsComparison } from './KeyMetricsComparison'
import { KeywordComparison } from './KeywordComparison'
import { NewComparisonPayload } from './NewComparisonModal'
import { lastCompleteMonths, shiftBack } from './periods'
import { SentimentSplit } from './SentimentSplit'
import { TopSources } from './TopSources'
import styles from './campaignDataComparison.module.scss'

interface CampaignDataComparisonProps {
  backUrl: string
  campaignId?: string
  campaignName?: string
  date?: string
  onViewChange: (view: CampaignDetailView) => void
  view: CampaignDetailView
}

// Feature-local copy, mirroring how InboxPreview keeps its own tab/empty-state
// strings beside the component rather than in the shared dictionary.
const COPY = {
  computingSubtitle:
    "We're crunching the latest numbers for your selected periods. " +
    'This will only take a moment.',
  computingTitle: 'Computing Your Comparison',
  downloadFailed:
    'The comparison report could not be generated. Please try again.',
  emptyStateSubtitle:
    'Select the dates you want to analyze in the fields above to compare metrics and performance.',
  emptyStateTitle: 'Start Comparing Your Data',
  exportComparison: 'Export Comparison',
  // The deck is rendered on demand and takes seconds; a button that says
  // nothing for that long reads as broken, and a second press queues a second
  // render the server may refuse.
  exportingComparison: 'Preparing PDF…',
  failedStateSubtitle:
    'An unexpected error occurred while computing your comparison. Please try again. ' +
    'If the issue persists, contact support.',
  failedStateTitle: 'Couldn’t Load Comparison',
  shortPeriodButton: 'Compare Longer Periods',
  shortPeriodSubtitle:
    'The selected date ranges are too short to generate reliable comparison ' +
    'insights. For more meaningful trends and recommendations, compare longer ' +
    'periods such as a month or a quarter.',
  shortPeriodTitle: 'Short Comparison Period',
  startComparison: 'Start Comparison',
  tryAgain: 'Try Again',
}

const RANGE_FORMAT = 'MMM D, YYYY'

const formatRange = (start: moment.Moment, end: moment.Moment) =>
  `${start.format(RANGE_FORMAT)} – ${end.format(RANGE_FORMAT)}`

// A couple of days either side gives the report nothing to trend on, so the
// client stops before spending a run to find that out.
const MIN_COMPARISON_DAYS = 7

interface Range {
  end: moment.Moment
  start: moment.Moment
}

// Calendar days, not instants: the API reads these as UTC days, and an ISO
// timestamp from a picker east of UTC floors to the previous day.
const API_DATE_FORMAT = 'YYYY-MM-DD'

const toWindow = ({ end, start }: Range) => ({
  from: start.format(API_DATE_FORMAT),
  to: end.format(API_DATE_FORMAT),
})

export const CampaignDataComparison: FC<CampaignDataComparisonProps> = ({
  backUrl,
  campaignId,
  campaignName,
  date,
  onViewChange,
  view,
}) => {
  // The first-run screen's own selection. Once a campaign has history, the
  // modal carries its own copy of this and these stay untouched.
  const [comparisonDates, setComparisonDates] = useState<Range>()
  const [comparisonRange, setComparisonRange] = useState<string>()
  const [primaryDates, setPrimaryDates] = useState<Range>()
  const [primaryRange, setPrimaryRange] = useState<string>()
  const [quickCompare, setQuickCompare] = useState<string>()
  const [isShortPeriod, setIsShortPeriod] = useState(false)

  // The comparison being read. The report is computed by the backend when the
  // row is opened, so the query's own lifecycle — loading, error, data —
  // decides what the tab shows.
  const [openId, setOpenId] = useState<string>()
  // The report's own export button; the list's rows track this inside the
  // history panel.
  const [isDownloading, setIsDownloading] = useState(false)

  const { data: listData, isLoading } = useGetDataComparisonsQuery(campaignId)
  const {
    data: openData,
    isError: isOpenError,
    refetch: refetchOpen,
  } = useGetDataComparisonQuery(campaignId, openId)

  const createMutation = useCreateDataComparisonMutation(campaignId)
  const deleteMutation = useDeleteDataComparisonMutation(campaignId)

  const comparisons = listData?.data ?? []
  const open = openData?.data
  const report = open?.result ?? null

  // Opening starts the moment the create request leaves, and lasts until the
  // computed report has actually been read back. Without this the list refetch
  // landed first and the tab flashed the history for an instant before
  // settling on the report.
  const isOpening =
    createMutation.isPending || (!!openId && !open && !isOpenError)

  const startComparison = (payload: NewComparisonPayload) => {
    setIsShortPeriod(false)

    createMutation.mutate(payload, {
      // Watching the new row is what turns the tab into the report screen.
      onSuccess: (response) => setOpenId(response.data.id),
    })
  }

  // The PDF is rendered by the backend on demand — the same numbers the screen
  // shows, printed — so a failure here is a compute or render error, not a
  // missing file.
  const handleDownload = async (comparisonId: string) => {
    if (!campaignId) {
      return
    }

    setIsDownloading(true)

    const comparison = comparisons.find((row) => row.id === comparisonId)
    const name = comparison
      ? `${comparison.primaryRange.from} vs ${comparison.comparisonRange.from}`
      : comparisonId

    try {
      await DataComparisonApiService.download(
        campaignId,
        comparisonId,
        `data-comparison-${name}.pdf`
      )
    } catch {
      toast.error(COPY.downloadFailed)
    } finally {
      // Whatever happened, the button stops claiming to be working.
      setIsDownloading(false)
    }
  }

  const handleDelete = (comparisonId: string) => {
    // Nothing left to read once it is gone; leaving it open would hold the tab
    // on a report that no longer exists.
    if (comparisonId === openId) {
      setOpenId(undefined)
    }

    deleteMutation.mutate(comparisonId)
  }

  // The first-run screen has its own Start Comparison rather than the modal.
  const handleStartComparison = () => {
    if (!primaryDates || !comparisonDates) {
      return
    }

    // Inclusive of both endpoints, so Jul 1 – Jul 2 counts as 2 days.
    const days = primaryDates.end.diff(primaryDates.start, 'days') + 1

    if (days < MIN_COMPARISON_DAYS) {
      setIsShortPeriod(true)
      return
    }

    startComparison({
      comparison: toWindow(comparisonDates),
      primary: toWindow(primaryDates),
      quickCompare: quickCompare ?? null,
    })
  }

  // Back steps through this tab's own screens before leaving it: an open
  // report returns to the history list, and from the list — or the first-run
  // setup — it goes to the campaign's General Overview. Without this the
  // chevron jumped straight out to the campaigns list from anywhere inside.
  const handleBack = () => {
    if (openId) {
      setOpenId(undefined)
      return
    }

    onViewChange('general')
  }

  const embeddedHeader = (
    <CampaignDetailHeader
      backUrl={backUrl}
      campaignName={campaignName}
      className={styles.embeddedHeader}
      date={date ? `From ${date}` : undefined}
      onBack={handleBack}
      onViewChange={onViewChange}
      parent="Campaigns"
      showActions={false}
      view={view}
    />
  )

  // A report being computed owns the whole tab, as does a compute that failed.
  if (isOpening) {
    return (
      <div className={styles.card}>
        {embeddedHeader}

        <div className={styles.emptyState}>
          <AnalyzingIcon className={styles.generatingIcon} />
          <h3 className={cn(styles.emptyStateTitle, styles.generatingTitle)}>
            {COPY.computingTitle}
          </h3>
          <p className={styles.generatingSubtitle}>{COPY.computingSubtitle}</p>
        </div>
      </div>
    )
  }

  if (openId && isOpenError) {
    return (
      <div className={styles.card}>
        {embeddedHeader}

        <div className={styles.emptyState}>
          <WarningTriangleIcon
            className={cn(styles.warningIcon, styles.warningIconFailed)}
          />
          <h3 className={styles.emptyStateTitle}>{COPY.failedStateTitle}</h3>
          <p className={styles.failedStateSubtitle}>
            {COPY.failedStateSubtitle}
          </p>
          <button
            className={styles.startButton}
            onClick={() => refetchOpen()}
            type="button"
          >
            {COPY.tryAgain}
          </button>
        </div>
      </div>
    )
  }

  // The report, computed from the saved windows. The periods are the saved
  // definition, so the filters read it back rather than offering a fresh
  // selection.
  if (report) {
    return (
      <>
        <div className={cn(styles.card, styles.cardHistory)}>
          {embeddedHeader}

          {/* The report's own copy of the row action: on this screen the list
              is behind you, so the deck has to be reachable from the report it
              prints. Same endpoint the row's download button calls. */}
          <button
            className={styles.exportButton}
            disabled={isDownloading}
            onClick={() => open && handleDownload(open.id)}
            type="button"
          >
            <ExportIcon
              className={cn(
                styles.exportIcon,
                isDownloading && styles.exportIconWorking
              )}
            />
            {isDownloading ? COPY.exportingComparison : COPY.exportComparison}
          </button>

          <span className={styles.divider} />

          <ComparisonFilters
            activeQuickCompare={open?.quickCompare ?? undefined}
            compareWith={report.comparisonLabel}
            // The run is finished; its two periods are what it was built from.
            disabled
            onPrimaryApply={() => undefined}
            primaryRange={report.primaryLabel}
          />

          <span className={styles.detailsSpacer} />

          <KeyMetricsComparison
            comparisonLabel={report.comparisonLabel}
            insight={report.insight}
            metrics={report.metrics}
            primaryLabel={report.primaryLabel}
          />
        </div>

        {/* Each section of the report is its own 1170px card, so the chart is
            a sibling rather than another block inside the one above. */}
        <SentimentSplit data={report.sentimentSplit} />

        <DailyMentions data={report.dailyMentions} />

        <KeywordComparison
          comparisonLabel={report.comparisonLabel}
          keywords={report.keywords}
          primaryLabel={report.primaryLabel}
        />

        <TopSources
          comparisonLabel={report.comparisonLabel}
          primaryLabel={report.primaryLabel}
          sources={report.sources}
        />
      </>
    )
  }

  // Nothing open: the history list. Its header sits on the page background
  // rather than inside the card, so the widget reads as the page's one card.
  // `isLoading` holds the list open — "you have no comparisons" is a claim,
  // and it is not known until the list has arrived.
  if (isLoading || comparisons.length) {
    return (
      <>
        <CampaignDetailHeader
          backUrl={backUrl}
          campaignName={campaignName}
          compact
          date={date ? `From ${date}` : undefined}
          onBack={handleBack}
          onViewChange={onViewChange}
          parent="Campaigns"
          showActions={false}
          view={view}
        />

        <div className={cn(styles.card, styles.cardHistory)}>
          <ComparisonHistory
            comparisons={comparisons}
            isLoading={isLoading}
            onAddNew={startComparison}
            onDelete={handleDelete}
            onDownload={handleDownload}
            onOpen={setOpenId}
          />
        </div>
      </>
    )
  }

  // First run: no comparisons yet, so the periods are picked on the page
  // itself rather than in the modal the history list opens.
  return (
    <div className={styles.card}>
      {embeddedHeader}

      <span className={styles.divider} />

      <ComparisonFilters
        activeQuickCompare={quickCompare}
        compareWith={comparisonRange}
        onCompareApply={(start, end) => {
          setComparisonDates({ end, start })
          setComparisonRange(formatRange(start, end))
          // A hand-picked range is no longer one of the shortcuts.
          setQuickCompare(undefined)
        }}
        onPrimaryApply={(start, end) => {
          setPrimaryDates({ end, start })
          setPrimaryRange(formatRange(start, end))
        }}
        onQuickCompare={({ label, months }) => {
          // With no primary period chosen the shortcut picks one — the last
          // `months` complete calendar months, which is what its label says —
          // rather than doing nothing. Silently ignoring the click made the
          // pills look disabled on a campaign with no comparisons yet.
          const base = primaryDates ?? lastCompleteMonths(months)

          if (!primaryDates) {
            setPrimaryDates(base)
            setPrimaryRange(formatRange(base.start, base.end))
          }

          const shifted = shiftBack(base, months)

          setQuickCompare(label)
          setComparisonDates(shifted)
          setComparisonRange(formatRange(shifted.start, shifted.end))
        }}
        primaryRange={primaryRange}
      />

      <span className={styles.divider} />

      {/* Unlike the failure screen, a too-short range keeps the filters on
          screen — the fix is one picker away, so the mock leaves them there. */}
      {isShortPeriod ? (
        <div className={styles.emptyState}>
          <WarningTriangleIcon
            className={cn(styles.warningIcon, styles.warningIconShortPeriod)}
          />
          <h3 className={styles.emptyStateTitle}>{COPY.shortPeriodTitle}</h3>
          <p className={styles.shortPeriodSubtitle}>
            {COPY.shortPeriodSubtitle}
          </p>
          <button
            className={styles.startButton}
            onClick={() => setIsShortPeriod(false)}
            type="button"
          >
            {COPY.shortPeriodButton}
          </button>
        </div>
      ) : (
        <div className={styles.emptyState}>
          {/* The asset carries its own rounded backdrop, so no wrapper. */}
          <EmptyComparisonIcon className={styles.emptyStateIcon} />
          <h3 className={styles.emptyStateTitle}>{COPY.emptyStateTitle}</h3>
          <p className={styles.emptyStateSubtitle}>{COPY.emptyStateSubtitle}</p>
          <button
            className={styles.startButton}
            disabled={!primaryDates || !comparisonDates}
            onClick={handleStartComparison}
            type="button"
          >
            {COPY.startComparison}
          </button>
        </div>
      )}
    </div>
  )
}
